import {
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { IconContextButton } from '@monorepo/shared-lib';
import { Input, Typography } from 'antd';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { MessageStates } from 'containers/MessageBox';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { DateOptions } from 'store/api/enums';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import styled, { createGlobalStyle } from 'styled-components';
import { getDateOptionLabel } from 'utils/display-enum-label';
import { Preference, PreferenceActions } from 'utils/preferences';
import { FilterView } from './FilterViewManager';

const CustomDrawerStyle = createGlobalStyle`
  /* Only remove bottom padding when this specific form is in the drawer */
  .ant-drawer-body:has(.filter-views-wrapper) {
    padding-bottom: 0 !important;
  }
`;

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  //min-height: calc(100vh - 130px);
  height: calc(100vh - 160px);
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  .top-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .bottom-section {
    flex-shrink: 0;
    padding: 16px 0;
    margin-top: auto;
  }
`;

const ViewsListWrapper = styled.div<{ deduct: number }>`
  max-height: calc(100vh - ${({ deduct }) => deduct}px);
  min-height: 45px;
  overflow-y: auto;
  background: var(--color-surface-base, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 0 8px 12px 8px;
  margin-bottom: 15px;
  /* Thinner, modern scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const ViewItem = styled.div`
  font-size: 12px;
  margin-bottom: 8px;
  user-select: none;
  border-bottom: 1px solid var(--color-border, #e8f0f0);
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

const ViewItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

const FilterContents = styled.div`
  padding: 8px 0 8px 24px;
  background-color: var(--color-surface, #f9f9f9);
  border-top: 1px solid var(--color-border, #e8f0f0);
  font-size: 11px;
  color: var(--color-text-muted, #666);
  line-height: 1.6;
`;

const ViewItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ViewItemRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ViewText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  font-weight: 500;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-base, var(--color-text-dark, #042a2a));
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border, #dbeae3);
  margin-bottom: 8px;
`;

const ColumnHeaderLeft = styled.div`
  flex: 1;
`;

const ColumnHeaderRight = styled.div`
  flex-shrink: 0;
  width: 80px;
  text-align: right;
`;

interface FilterViewsDrawerProps {
  views: FilterView[];
  onUpdate: (updatedViews: FilterView[]) => void;
  onClose?: () => void;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
  currentUser?: { userUuid: string };
}

const FILTER_VIEWS_PREFERENCE = 'submissions-filter-views';

export const FilterViewsDrawer: React.FC<FilterViewsDrawerProps> = ({
  views,
  onUpdate,
  onClose,
  handlePreferences,
  currentUser,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [localViews, setLocalViews] = useState<FilterView[]>(views);
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editingViewName, setEditingViewName] = useState('');
  const [expandedViewId, setExpandedViewId] = useState<string | null>(null);

  const { Text } = Typography;

  const handleSave = () => {
    // Save to preferences
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_VIEWS_PREFERENCE,
        value: localViews,
      });
    }

    // Update parent
    onUpdate(localViews);

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-views-updated'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));

    if (onClose) {
      onClose();
    }
  };

  const handleRename = (viewId: string, newName: string) => {
    // Prevent renaming standard views
    const view = localViews.find((v) => v.id === viewId);
    if (view?.isStandard) {
      return;
    }

    if (!newName.trim()) {
      const message: MessageData = {
        title: t('common.error'),
        description: t('submissions.filter-view-name-required'),
        type: MessageStates.ERROR,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    const updatedViews = localViews.map((view) =>
      view.id === viewId ? { ...view, name: newName.trim() } : view
    );
    setLocalViews(updatedViews);
    setEditingViewId(null);
    setEditingViewName('');

    // Save immediately
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_VIEWS_PREFERENCE,
        value: updatedViews,
      });
    }
    onUpdate(updatedViews);

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-view-renamed'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleDelete = (viewId: string) => {
    // Prevent deleting standard views
    const view = localViews.find((v) => v.id === viewId);
    if (view?.isStandard) {
      return;
    }

    const updatedViews = localViews.filter((v) => v.id !== viewId);
    setLocalViews(updatedViews);

    // Save immediately
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: FILTER_VIEWS_PREFERENCE,
        value: updatedViews,
      });
    }
    onUpdate(updatedViews);

    const message: MessageData = {
      title: t('common.success-exclamation'),
      description: t('submissions.filter-view-deleted'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const handleStartEdit = (view: FilterView) => {
    // Prevent editing standard views
    if (view.isStandard) {
      return;
    }
    setEditingViewId(view.id);
    setEditingViewName(view.name);
  };

  const handleCancelEdit = () => {
    setEditingViewId(null);
    setEditingViewName('');
  };

  // Update local views when props change
  React.useEffect(() => {
    setLocalViews(views);
  }, [views]);

  // Format filter object into readable text
  const formatFilterContents = (filters: any): string[] => {
    if (!filters || typeof filters !== 'object') {
      return [t('submissions.no-filters')];
    }

    const filterLabels: Record<string, string> = {
      mappingName: t('main.taxReturn'),
      organisation: t('common.entity'),
      country: t('common.country'),
      nextDueDate: t('main.dueDate'),
      preparer: t('main.preparer'),
      reviewer: t('main.reviewer'),
      client: t('main.client'),
      status: t('main.status'),
      person: t('main.filterLabels.person'),
    };

    const formattedFilters: string[] = [];

    Object.keys(filters).forEach((key) => {
      const filterValue = filters[key]?.value;
      if (!filterValue) return;

      const label = filterLabels[key] || key;
      let displayValue = '';

      if (key === 'person' && typeof filterValue === 'string') {
        // Handle person filter: "preparer:name,reviewer:name,client:name"
        const rolePersonPairs = filterValue.split(',');
        const roleLabels: Record<string, string> = {
          preparer: t('main.preparer'),
          reviewer: t('main.reviewer'),
          client: t('main.client'),
        };
        const formattedRoles = rolePersonPairs.map((pair) => {
          const [role, name] = pair.split(':');
          return `${roleLabels[role] || role}: ${name}`;
        });
        displayValue = formattedRoles.join(', ');
      } else if (key === 'nextDueDate') {
        if (Object.values(DateOptions).includes(filterValue as DateOptions)) {
          displayValue = getDateOptionLabel(String(filterValue), t);
        } else if (Array.isArray(filterValue) && filterValue.length === 2) {
          // Date range
          const startDate = filterValue[0];
          const endDate = filterValue[1];
          if (startDate && endDate) {
            displayValue = `${new Date(
              startDate
            ).toLocaleDateString()} - ${new Date(
              endDate
            ).toLocaleDateString()}`;
          } else if (startDate) {
            displayValue = t('submissions.date-from', {
              date: new Date(startDate).toLocaleDateString(),
            });
          } else if (endDate) {
            displayValue = t('submissions.date-until', {
              date: new Date(endDate).toLocaleDateString(),
            });
          }
        } else {
          displayValue = String(filterValue);
        }
      } else if (Array.isArray(filterValue)) {
        displayValue = filterValue.join(', ');
      } else {
        displayValue = String(filterValue);
      }

      if (displayValue) {
        formattedFilters.push(`${label}: ${displayValue}`);
      }
    });

    return formattedFilters.length > 0
      ? formattedFilters
      : [t('submissions.no-filters')];
  };

  return (
    <>
      <CustomDrawerStyle />
      <Wrapper className="filter-views-wrapper">
        <div className="top-section">
          <ViewsListWrapper
            deduct={120}
            tabIndex={0}
            role="region"
            aria-label={t('submissions.edit-filter-views-title')}
          >
            <div style={{ paddingTop: 12 }}>
              {localViews.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: 'var(--color-text-muted, #5a6a6a)',
                  }}
                >
                  {t('submissions.no-filter-views-saved')}
                </div>
              ) : (
                <>
                  <ColumnHeader>
                    <ColumnHeaderLeft>
                      {t('submissions.view-name-header')}
                    </ColumnHeaderLeft>
                    <ColumnHeaderRight>
                      {t('submissions.actions-header')}
                    </ColumnHeaderRight>
                  </ColumnHeader>
                  {localViews.map((view) => (
                    <ViewItem key={view.id}>
                      <ViewItemHeader>
                        <ViewItemLeft>
                          <StyledTooltip
                            title={
                              expandedViewId === view.id
                                ? t('submissions.hide-filter-contents')
                                : t('submissions.show-filter-contents')
                            }
                            placement="top"
                          >
                            <span
                              role="button"
                              tabIndex={0}
                              aria-label={
                                expandedViewId === view.id
                                  ? t('submissions.hide-filter-contents')
                                  : t('submissions.show-filter-contents')
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedViewId(
                                  expandedViewId === view.id ? null : view.id
                                );
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedViewId(
                                    expandedViewId === view.id ? null : view.id
                                  );
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                marginRight: '8px',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              {expandedViewId === view.id ? (
                                <MinusOutlined
                                  style={{
                                    fontSize: '12px',
                                    color: 'var(--color-primary, #41aaaf)',
                                  }}
                                />
                              ) : (
                                <PlusOutlined
                                  style={{
                                    fontSize: '12px',
                                    color: 'var(--color-primary, #41aaaf)',
                                  }}
                                />
                              )}
                            </span>
                          </StyledTooltip>
                          {editingViewId === view.id ? (
                            <Input
                              value={editingViewName}
                              aria-label={t('submissions.view-name-label')}
                              onChange={(e) =>
                                setEditingViewName(e.target.value)
                              }
                              onPressEnter={() =>
                                handleRename(view.id, editingViewName)
                              }
                              onBlur={() => handleCancelEdit()}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              style={{ flex: 1, fontSize: '12px' }}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <ViewText>{view.name}</ViewText>
                          )}
                        </ViewItemLeft>
                        <ViewItemRight onClick={(e) => e.stopPropagation()}>
                          {editingViewId === view.id ? (
                            <StyledTooltip
                              title={t(
                                'submissions.save-filter-view-changes-tooltip'
                              )}
                              placement="top"
                            >
                              <IconContextButton
                                type="text"
                                aria-label={t(
                                  'submissions.save-filter-view-changes-tooltip'
                                )}
                                icon={
                                  <SaveOutlined
                                    style={{ width: '18px', height: '18px' }}
                                  />
                                }
                                onClick={() =>
                                  handleRename(view.id, editingViewName)
                                }
                              />
                            </StyledTooltip>
                          ) : (
                            <>
                              <StyledTooltip
                                title={
                                  view.isStandard
                                    ? t(
                                        'submissions.standard-views-cannot-rename-tooltip'
                                      )
                                    : t('submissions.rename-filter-view-tooltip')
                                }
                                placement="top"
                              >
                                <span>
                                  <IconContextButton
                                    type="text"
                                    aria-label={t(
                                      'submissions.rename-filter-view-tooltip'
                                    )}
                                    icon={
                                      <EditOutlined
                                        style={{
                                          width: '18px',
                                          height: '18px',
                                        }}
                                      />
                                    }
                                    onClick={() => handleStartEdit(view)}
                                    disabled={view.isStandard}
                                  />
                                </span>
                              </StyledTooltip>
                              {view.isStandard ? (
                                <StyledTooltip
                                  title={t(
                                    'submissions.standard-views-cannot-delete-tooltip'
                                  )}
                                  placement="top"
                                >
                                  <span>
                                    <IconContextButton
                                      type="text"
                                      aria-label={t(
                                        'submissions.delete-filter-view-tooltip'
                                      )}
                                      icon={
                                        <DeleteOutlined
                                          style={{
                                            width: '18px',
                                            height: '18px',
                                          }}
                                        />
                                      }
                                      disabled={true}
                                    />
                                  </span>
                                </StyledTooltip>
                              ) : (
                                <StyledPopconfirm
                                  title={t('submissions.delete-filter-view-title')}
                                  description={t(
                                    'submissions.delete-filter-view-description',
                                    { viewName: view.name }
                                  )}
                                  onConfirm={() => handleDelete(view.id)}
                                  okText={t('common.delete')}
                                  cancelText={t('common.cancel')}
                                  placement="topRight"
                                  overlayStyle={{
                                    width: '400px',
                                    maxWidth: '400px',
                                  }}
                                >
                                  <StyledTooltip
                                    title={t(
                                      'submissions.delete-filter-view-tooltip'
                                    )}
                                    placement="top"
                                  >
                                    <span>
                                      <IconContextButton
                                        type="text"
                                        aria-label={t(
                                          'submissions.delete-filter-view-tooltip'
                                        )}
                                        icon={
                                          <DeleteOutlined
                                            style={{
                                              width: '18px',
                                              height: '18px',
                                            }}
                                          />
                                        }
                                      />
                                    </span>
                                  </StyledTooltip>
                                </StyledPopconfirm>
                              )}
                            </>
                          )}
                        </ViewItemRight>
                      </ViewItemHeader>
                      {expandedViewId === view.id && (
                        <FilterContents>
                          {formatFilterContents(view.filters).map(
                            (filterText, index) => (
                              <div key={index}>{filterText}</div>
                            )
                          )}
                        </FilterContents>
                      )}
                    </ViewItem>
                  ))}
                </>
              )}
            </div>
          </ViewsListWrapper>
        </div>
      </Wrapper>
    </>
  );
};
